/**
 * Geometry and hit-testing of the collapsed motion columns (W2-04a motion-columns; DESIGN.md §4.7, ARCHITECTURE.md
 * §7.7 "hit-testing is analytic").
 *
 * Geometry of a column (CSS px, column-local):
 *  - its lanes split the width equally, `PAIR_GAP` px apart (one lane, or the two lanes of a Guided pair);
 *  - a numeric lane maps its value range [lo, hi] to [x0 + LANE_PAD, x1 - LANE_PAD] (keys at the ends of the range
 *    stay grabbable); time runs up the shared axis (`axis.yAtBeat`);
 *  - a key is a diamond at x(vOut); a JUMP key (vIn !== vOut) is two half-diamonds: the arrive half at x(vIn)
 *    pointing left and the leave half at x(vOut) pointing right, so each value can be dragged on its own;
 *  - colour lanes draw keys on the lane centre, text lanes near the lane's left edge (their labels run right).
 *
 * Hit-testing (`hitTestColumn`): keys and half-diamonds first (within KEY_PICK_PX, the nearest wins), then the
 * segment under the pointer (numeric lanes: within CURVE_PICK_PX of the drawn curve or of the straight line between
 * its keys; colour and text lanes: anywhere between two keys), else 'empty'. Keys are found by binary search on the
 * sorted beats, so a hit costs O(log n) whatever the track length.
 *
 * `keysInRect` answers the host's marquee (`itemsInRect`): keys whose beat lies in [b0, b1] and whose x (either
 * half of a jump key) lies in [x0, x1]; infinite bounds (a gutter time-range selection) are compared directly.
 */

import { Search } from '../../../core/math/search';
import type { KeyId } from '../../../core/model/types';
import type { TimeAxis } from '../../types';
import type { SampledCurve } from '../curveCache';
import type { MotionColumnScene, MotionHit, MotionKeyView, MotionLaneScene } from './motionScene';

/** Gap between the two lanes of a pair column. */
export const PAIR_GAP = 6;
/** Horizontal padding inside a lane so keys at the ends of the value range stay grabbable. */
export const LANE_PAD = 9;
/** Key glyph size (diamond width and height). */
export const MOTION_KEY_SIZE = 9;
/** Pick tolerance around a key centre. */
export const KEY_PICK_PX = 7;
/** Pick tolerance around a segment's curve. */
export const CURVE_PICK_PX = 6;
/** Text lanes: x offset of the keys from the lane's left edge. */
export const TEXT_KEY_INSET = 4;

/** Maps raw values to column x inside a numeric lane. */
export interface ValueMap {
  readonly lo: number;
  readonly hi: number;
  /** Pixel span of the range (inner lane edges). */
  readonly x0: number;
  readonly x1: number;
  toX(v: number): number;
  toValue(x: number): number;
  /** Raw value units per CSS px (> 0). */
  readonly perPx: number;
}

export function createValueMap(lo: number, hi: number, x0: number, x1: number): ValueMap {
  let a = Number.isFinite(lo) ? lo : 0;
  let b = Number.isFinite(hi) ? hi : 1;
  if (b < a) [a, b] = [b, a];
  if (b - a < 1e-9) {
    const pad = Math.max(1e-3, Math.abs(a) * 0.1, 1);
    a -= pad;
    b += pad;
  }
  const left = Math.min(x0, x1);
  const right = Math.max(x0, x1, left + 1);
  const span = right - left;
  const perPx = (b - a) / span;
  return {
    lo: a,
    hi: b,
    x0: left,
    x1: right,
    perPx,
    toX: (v) => left + ((v - a) / (b - a)) * span,
    toValue: (x) => a + ((x - left) / span) * (b - a),
  };
}

/** Horizontal placement of one lane. */
export interface LaneRect {
  readonly index: number;
  readonly x0: number;
  readonly x1: number;
  /** Numeric lanes: their value map; null for colour and text lanes. */
  readonly map: ValueMap | null;
}

/** Lane rectangles of a column of `widthPx`. */
export function laneRects(scene: MotionColumnScene, widthPx: number): LaneRect[] {
  const n = Math.max(1, scene.lanes.length);
  const w = Math.max(1, (widthPx - PAIR_GAP * (n - 1)) / n);
  const out: LaneRect[] = [];
  scene.lanes.forEach((lane, i) => {
    const x0 = i * (w + PAIR_GAP);
    const x1 = x0 + w;
    const pad = Math.min(LANE_PAD, w / 4);
    const map =
      lane.kind === 'number' ? createValueMap(lane.range?.lo ?? -1, lane.range?.hi ?? 1, x0 + pad, x1 - pad) : null;
    out.push({ index: i, x0, x1, map });
  });
  return out;
}

/** x of a key's arrive ('in') or leave ('out') glyph in its lane. */
export function keyX(lane: MotionLaneScene, rect: LaneRect, k: MotionKeyView, part: 'in' | 'out'): number {
  if (lane.kind === 'color') return (rect.x0 + rect.x1) / 2;
  if (lane.kind === 'text') return rect.x0 + Math.min(LANE_PAD, (rect.x1 - rect.x0) / 4) + TEXT_KEY_INSET;
  const map = rect.map;
  if (map === null) return (rect.x0 + rect.x1) / 2;
  const v = part === 'in' ? k.vIn : k.vOut;
  return Number.isFinite(v) ? map.toX(v) : (rect.x0 + rect.x1) / 2;
}

/** Index of the lane containing column x (the gap between lanes belongs to the nearer lane), or -1. */
export function laneAtX(rects: readonly LaneRect[], x: number): number {
  for (const r of rects) {
    if (x >= r.x0 - PAIR_GAP / 2 && x <= r.x1 + PAIR_GAP / 2) return r.index;
  }
  return -1;
}

/** Sorted beats of a lane's keys (cached per keys array). */
const beatCache = new WeakMap<readonly MotionKeyView[], Float64Array>();
export function keyBeats(keys: readonly MotionKeyView[]): Float64Array {
  let b = beatCache.get(keys);
  if (b === undefined) {
    b = Float64Array.from(keys, (k) => k.beat);
    beatCache.set(keys, b);
  }
  return b;
}

/** Index of the segment containing `beat` (the last key with key.beat <= beat), or -1 before the first key. */
export function segmentIndexAt(keys: readonly MotionKeyView[], beat: number): number {
  const beats = keyBeats(keys);
  return Search.upperBound(beats, beat) - 1;
}

/** Value of a sampled curve at a beat (linear between samples; NaN outside or where the curve is absent). */
export function curveValueAt(curve: SampledCurve, beat: number): number {
  const f = (beat - curve.beat0) / curve.step;
  const i = Math.floor(f);
  if (i < 0 || i >= curve.values.length) return Number.NaN;
  const a = curve.values[i];
  const b = curve.values[i + 1];
  if (a === undefined) return Number.NaN;
  if (b === undefined || !Number.isFinite(b)) return a;
  if (!Number.isFinite(a)) return b;
  return a + (b - a) * (f - i);
}

function distance(ax: number, ay: number, bx: number, by: number): number {
  return Math.hypot(ax - bx, ay - by);
}

/** Pointer hit on a column (column-local CSS px). */
export function hitTestColumn(
  scene: MotionColumnScene,
  widthPx: number,
  axis: TimeAxis,
  x: number,
  y: number,
): MotionHit {
  const rects = laneRects(scene, widthPx);
  const li = laneAtX(rects, x);
  const empty = (lane: number): MotionHit => ({ kind: 'empty', lane, index: -1, cursor: 'crosshair' });
  if (li < 0) return empty(-1);
  const lane = scene.lanes[li];
  const rect = rects[li];
  if (lane === undefined || rect === undefined || lane.raw !== null) return empty(li);
  const keys = lane.keys;
  if (keys.length === 0) return empty(li);
  const beats = keyBeats(keys);
  const beat = axis.beatAtY(y);
  const tol = KEY_PICK_PX / Math.max(1e-9, axis.pxPerBeat);
  const lo = Search.lowerBound(beats, beat - tol);
  const hi = Search.upperBound(beats, beat + tol, lo);
  let best: MotionHit | null = null;
  let bestD = Number.POSITIVE_INFINITY;
  for (let i = lo; i < hi; i++) {
    const k = keys[i];
    if (k === undefined) continue;
    const ky = axis.yAtBeat(k.beat);
    const parts: ReadonlyArray<readonly ['key' | 'keyIn' | 'keyOut', 'in' | 'out']> = k.jump
      ? [
          ['keyIn', 'in'],
          ['keyOut', 'out'],
        ]
      : [['key', 'out']];
    for (const [kind, part] of parts) {
      let kx = keyX(lane, rect, k, part);
      // A half-diamond's mass sits on its pointing side.
      if (kind === 'keyIn') kx -= MOTION_KEY_SIZE / 4;
      else if (kind === 'keyOut') kx += MOTION_KEY_SIZE / 4;
      const d = distance(kx, ky, x, y);
      if (d <= KEY_PICK_PX && d < bestD) {
        bestD = d;
        best = { kind, lane: li, index: i, key: k.id, cursor: 'grab' };
      }
    }
  }
  if (best !== null) return best;

  const si = Search.upperBound(beats, beat) - 1;
  if (si < 0 || si >= keys.length - 1) return empty(li);
  const k0 = keys[si] as MotionKeyView;
  const k1 = keys[si + 1] as MotionKeyView;
  const segment: MotionHit = { kind: 'segment', lane: li, index: si, key: k0.id, cursor: 'pointer' };
  if (lane.kind !== 'number') return x >= rect.x0 && x <= rect.x1 ? segment : empty(li);
  // Distance to the drawn curves and to the straight line between the keys.
  let dMin = Number.POSITIVE_INFINITY;
  for (const c of lane.curves) {
    if (c.style === 'sigma') continue;
    const v = curveValueAt(c.curve, beat);
    if (Number.isFinite(v) && rect.map !== null) dMin = Math.min(dMin, Math.abs(rect.map.toX(v) - x));
  }
  const x0 = keyX(lane, rect, k0, 'out');
  const x1 = k0.ease.kind === 'hold' ? x0 : keyX(lane, rect, k1, 'in');
  const t = (beat - k0.beat) / Math.max(1e-12, k1.beat - k0.beat);
  dMin = Math.min(dMin, Math.abs(x0 + (x1 - x0) * Math.min(1, Math.max(0, t)) - x));
  return dMin <= CURVE_PICK_PX ? segment : empty(li);
}

/**
 * Keys inside a marquee: beat in [b0, b1] (f64) and x in [x0, x1] (column px; -Infinity / +Infinity leave that side
 * open). Lanes showing raw events take part with every key in the beat range.
 */
export function keysInRect(
  scene: MotionColumnScene,
  widthPx: number,
  b0: number,
  b1: number,
  x0: number,
  x1: number,
): KeyId[] {
  const lo = Math.min(b0, b1);
  const hiB = Math.max(b0, b1);
  const left = Math.min(x0, x1);
  const right = Math.max(x0, x1);
  const rects = laneRects(scene, widthPx);
  const out: KeyId[] = [];
  scene.lanes.forEach((lane, li) => {
    const rect = rects[li];
    if (rect === undefined) return;
    if (rect.x1 < left || rect.x0 > right) return;
    const beats = keyBeats(lane.keys);
    const i0 = Search.lowerBound(beats, lo);
    const i1 = Search.upperBound(beats, hiB, i0);
    for (let i = i0; i < i1; i++) {
      const k = lane.keys[i];
      if (k === undefined) continue;
      if (lane.raw !== null) {
        out.push(k.id);
        continue;
      }
      const xs = k.jump ? [keyX(lane, rect, k, 'in'), keyX(lane, rect, k, 'out')] : [keyX(lane, rect, k, 'out')];
      if (xs.some((kx) => kx >= left && kx <= right)) out.push(k.id);
    }
  });
  return out;
}

/** Key views of a lane inside the visible beat range, plus one on each side (segments crossing the edges). */
export function visibleKeyRange(keys: readonly MotionKeyView[], b0: number, b1: number): readonly [number, number] {
  const beats = keyBeats(keys);
  const i0 = Math.max(0, Search.lowerBound(beats, b0) - 1);
  const i1 = Math.min(keys.length, Search.upperBound(beats, b1) + 1);
  return [i0, i1];
}
